import {
  Card,
  CardBody,
  CardFooter,
  CardHeader,
  Text,
  VStack,
} from "@chakra-ui/react";
import { useStytchSession, useStytchUser } from "@stytch/react";
import {
  AuthFactorInfo,
  OtpAuthFactorInfo,
  TotpAuthFactorInfo,
  WebauthnAuthFactorInfo,
} from "features/auth/AuthFactor";
import { AuthHelpButton } from "features/auth/AuthHelpButton";
import { useAuthCancel, useAuthContinue } from "features/auth/AuthUtils";
import { AuthFactorButton } from "features/auth/factor/AuthFactorButtons";
import { AuthFactorRemember } from "features/auth/factor/AuthFactorRemember";
import { AuthFactorOtpSetup } from "features/auth/factor/otp/AuthFactorOtpSetup";
import { AuthFactorTotpSetup } from "features/auth/factor/totp/AuthFactorTotpSetup";
import { AuthFactorWebauthnSetup } from "features/auth/factor/webauthn/AuthFactorWebauthnSetup";
import { isStytchSessionExtended } from "features/auth/StytchUtils";
import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { CardCloseButton } from "shared/components/card/CardCloseButton";

const AuthFactorElements = {
  totp: AuthFactorTotpSetup,
  webauthn: AuthFactorWebauthnSetup,
  otp: AuthFactorOtpSetup,
  recovery: React.Fragment, // Not used here
};

export const AuthFactorSetup: React.FC<{
  onSuccess: () => void;
  onCancel: () => void;
  hideRememberOption?: boolean;
}> = ({ onSuccess, onCancel, hideRememberOption }) => {
  const { t } = useTranslation(["auth", "common"]);

  const { user: stytchUser } = useStytchUser();
  const { session: stytchSession } = useStytchSession();
  const hasTotp = Boolean(stytchUser?.totps.some((totp) => totp.verified));

  const [authFactor, setAuthFactor] = useState<AuthFactorInfo>();
  const [rememberDevice, setRememberDevice] = useState(
    () => !hideRememberOption || isStytchSessionExtended(stytchSession),
  );

  if (authFactor) {
    const AuthSetupElement = AuthFactorElements[authFactor.type];
    return (
      <AuthSetupElement
        onSuccess={onSuccess}
        onCancel={() => setAuthFactor(undefined)}
        rememberDevice={rememberDevice}
      />
    );
  }

  return (
    <Card width="md">
      <CardHeader>
        <Text textStyle="h4">{t("auth:addAnotherMethod")}</Text>
        <CardCloseButton onClick={onCancel} />
      </CardHeader>
      <CardBody>
        <VStack alignItems="stretch" mb="16px">
          {!hasTotp && (
            <AuthFactorButton
              info={TotpAuthFactorInfo}
              onClick={setAuthFactor}
            />
          )}
          <AuthFactorButton
            info={WebauthnAuthFactorInfo}
            onClick={setAuthFactor}
          />
          <AuthFactorButton info={OtpAuthFactorInfo} onClick={setAuthFactor} />
        </VStack>
        {!hideRememberOption && (
          <AuthFactorRemember
            rememberDevice={rememberDevice}
            setRememberDevice={setRememberDevice}
          />
        )}
      </CardBody>
      <CardFooter>
        <AuthHelpButton />
      </CardFooter>
    </Card>
  );
};

export const AuthFactorSetupPage: React.FC = () => {
  const authCancel = useAuthCancel();
  const authContinue = useAuthContinue();
  return <AuthFactorSetup onSuccess={authContinue} onCancel={authCancel} />;
};
